// MainAnimation.tsx
import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import Colors from "../../../ui/global/Colors";
import LabelRow from "../../../ui/components/LabelRow";
import PlayPauseOverlay from "../components/PlayPauseOverlay";
import LoadingSpinner from "../../../ui/components/LoadingSpinner";
import { AnimateActionType } from "../hooks/useAnimateActions";

const AnimationStage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start; 
    justify-content: flex-start;
    height: 100%;
    min-height: 0; 
    overflow: hidden;
    gap: 8px;
    flex: 1;

    @media (max-width: 768px) {
        width: 100%;
        height: auto;
        min-height: 300px;
        flex: none;
    }
`;

const AnimationFrameWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    flex: 1;
    min-height: 0;
    height: auto;
    border-radius: 6px;
    object-fit: contain;
    overflow: hidden;
    margin: 0 auto;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const White = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    width: auto;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    aspect-ratio: 1 / 1;
    background-color: ${Colors.white};
    border-radius: 2px;
    border: 1px solid ${Colors.grey1};
    margin: 0;
`;

const ImageContainer = styled(motion.div)<{ $isAnimation?: boolean; $isCurrentFrame?: boolean; $isOutsideRange?: boolean; $isHidden?: boolean; }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    aspect-ratio: 1 / 1;
    background-image: url("/assets/transparent-background.svg");
    background-size: cover;
    background-repeat: no-repeat;
    border-radius: 2px;
    border: ${props => props.$isCurrentFrame ? `1px solid ${Colors.grey6}` : `1px solid ${Colors.grey1}`};
    overflow: hidden;
    margin: 0;
    cursor: pointer;
    opacity: ${props => (props.$isOutsideRange || props.$isHidden ? 0.2 : 1)};

    ${props => !props.$isAnimation && `
        &:hover {
            border: 1px solid ${Colors.grey4};
        }
    `}
`;

const AnimationImage = styled.img<{ $isAnimation?: boolean }>`
    width: 100%;
    height: 100%;
    object-fit: contain;
    user-select: none;
    pointer-events: ${props => props.$isAnimation ? 'auto' : 'none'};
`;

const LoadingContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 100;
`;

interface MainAnimationProps {
    animationImages: string[];
    currentFrameIndex: number;
    startFrame: number;
    endFrame: number;
    hiddenFrames: number[];
    currentAction: AnimateActionType | null;
    overlayIcon: "play" | "pause";
    showOverlay: boolean;
    handlePlayPauseClick: (e?: React.MouseEvent<HTMLDivElement>) => void;
}

const MainAnimation = ({
    animationImages,
    currentFrameIndex,
    startFrame,
    endFrame,
    hiddenFrames,
    currentAction,
    overlayIcon,
    showOverlay,
    handlePlayPauseClick,
}: MainAnimationProps) => {
    return (
        <AnimationStage>
            <LabelRow label="Animation" />
            <AnimationFrameWrapper>
                {animationImages && animationImages.length > 0 ? (
                    <ImageContainer
                        $isAnimation={true}
                        $isOutsideRange={currentFrameIndex < startFrame || currentFrameIndex > endFrame}
                        $isHidden={hiddenFrames.includes(currentFrameIndex)}
                        onClick={() => handlePlayPauseClick()}
                    >
                        <AnimationImage
                            src={animationImages[currentFrameIndex]}
                            alt={`Animation Frame ${currentFrameIndex + 1}`}
                            $isAnimation={true}
                        />

                        <PlayPauseOverlay
                            overlayIcon={overlayIcon}
                            showOverlay={showOverlay}
                            onClick={handlePlayPauseClick}
                        />

                        {currentAction !== null && (
                            <LoadingContainer>
                                <LoadingSpinner colorMode="light" />
                            </LoadingContainer>
                        )}
                    </ImageContainer>
                ) : (
                    <White>
                        {currentAction !== null && (
                            <LoadingContainer>
                                <LoadingSpinner colorMode="light" />
                            </LoadingContainer>
                        )}
                    </White>
                )}
            </AnimationFrameWrapper>
        </AnimationStage>
    );
};

export default MainAnimation;